import React, { FormEvent, useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Chip,
  TextField,
  FormControl,
  useMediaQuery,
  Fab,
  Grid,
  Pagination,
  Alert,
  AlertTitle,
} from "@mui/material";
import CommonModal from "./HWSubmitModal";
import LayoutWithIcons from "./LayoutWithIcons";
import { useCharactersContext } from "../hooks/Context";
import Lessons from "../pages/Lessons";

interface Question {
  updatedBy: string;
  question: string;
  mediaUrl: string;
  difficultyLevel: string;
  answerType: number;
  active: boolean;
  marks: number;
  updatedDate: string;
  subjectId: number;
  questionBankId: number;
  classId: number;
  createdDate: string;
  createdBy: string;
  optionsList: any[];
  questionPaperId: number;
  uiType: number;
  remarks: string;
}

interface Layout14Props {
  Questions: Question[];
  sectionName: string;
}

interface QuestionState {
  questionIndex: number;
  draggedWord: string | null;
  droppedArticle: string | null;
}

const Layout14: React.FC<Layout14Props> = ({ Questions, sectionName }) => {
  //console.log("Layout14Questions", Questions);
  const isTablet = useMediaQuery("(max-width:820px)");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const currentQuestion = Questions[currentQuestionIndex];
  const sentences = currentQuestion?.question.split("<br>");
  //console.log("Current Question", currentQuestion);
  const questionText1 = sentences[0];
  const questionText2 = sentences[1];
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [modalOpen, setModalOpen] = useState(false);
  const { examAnswers, setExamAnswers } = useCharactersContext();
  // const [inputValues, setInputValues] = useState<string[]>([]);
  const [inputValues, setInputValues] = useState<string[]>(() => {
    const storageKey = `inputValuesLayout14_${currentQuestion.questionPaperId}`;
    const savedState = localStorage.getItem(storageKey);
    return savedState
      ? JSON.parse(savedState)
      : Array(Questions.length).fill("");
  });
  const [isCorrectSubmission, setIsCorrectSubmission] = useState<
    boolean | null
  >(null);
  const [showAlert, setShowAlert] = useState(false);
  const handleAlertClose = () => setShowAlert(false);
  setTimeout(handleAlertClose, 3000);

  const handleInputChange = (value: string, index: number) => {
    setInputValues((prevValues) => {
      const updatedValues = [...prevValues];
      updatedValues[index] = value;
      return updatedValues;
    });
  };

  const [selectedOptions, setSelectedOptions] = useState<string[][]>(() => {
    const storageKey = `selectedOptionsLayout14_${currentQuestion.questionPaperId}`;
    const savedOptions = localStorage.getItem(storageKey);
    return savedOptions
      ? JSON.parse(savedOptions)
      : Array(Questions.length).fill([]);
  });

  const handleOptionChange = (option: string) => {
    setSelectedOptions((prevSelectedOptions) => {
      const updatedOptions = [...prevSelectedOptions];

      updatedOptions[currentQuestionIndex] = [option];

      return updatedOptions;
    });
  };

  const handleNavigate = (direction: "previous" | "next") => {
    if (direction === "previous") {
      setCurrentQuestionIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    } else if (direction === "next") {
      setCurrentQuestionIndex((prevIndex) =>
        Math.min(prevIndex + 1, Questions.length - 1)
      );
    }
  };

  const { questionScores, setQuestionScores, isViewMode } =
    useCharactersContext();

  const handleFormSubmit = () => {
    setShowAlert(true);
    let isCorrectSubmission = false;
    let enteredWord = "";
    let correctAnswer = null;

    if (currentQuestion.optionsList?.length > 1) {
      correctAnswer = currentQuestion.optionsList
        .filter((option) => option.correctAnswer)
        .map((option) => option.option);

      const selectedForCurrentQuestion = selectedOptions[currentQuestionIndex];
      isCorrectSubmission = correctAnswer.every((option) =>
        selectedForCurrentQuestion.includes(option)
      );
    } else {
      enteredWord = inputValues[currentQuestionIndex] || "";

      correctAnswer = currentQuestion.optionsList[0].option;
      //console.log("correct answer", correctAnswer);

      isCorrectSubmission = enteredWord
        .toLowerCase()
        .includes(correctAnswer.toLowerCase());
    }

    if (isCorrectSubmission) {
      setModalOpen(true);
      const questionIndex = questionScores.findIndex(
        (item) => item.questionBankId === currentQuestion.questionBankId
      );

      // console.log(
      //   "currentQuestion.questionBankId",
      //   currentQuestion.questionBankId
      // );

      //console.log("currentQuestion.marks", currentQuestion.marks);

      if (questionIndex !== -1) {
        // Update the existing score
        const updatedScores = [...questionScores];
        updatedScores[questionIndex] = {
          questionBankId: currentQuestion.questionBankId,
          questionPaperId: currentQuestion.questionPaperId,
          score: currentQuestion.marks,
          layoutNumber: currentQuestion.uiType,
          sectionName: sectionName,
        };

        setQuestionScores(updatedScores);
      } else {
        // Add a new score for the question

        // console.log(
        //   "currentQuestion.questionBankId",
        //   currentQuestion.questionBankId
        // );

        //console.log("currentQuestion.marks", currentQuestion.marks);
        setQuestionScores((prevScores) => [
          ...prevScores,
          {
            questionBankId: currentQuestion.questionBankId,
            questionPaperId: currentQuestion.questionPaperId,
            score: currentQuestion.marks,
            layoutNumber: currentQuestion.uiType,
            sectionName: sectionName,
          },
        ]);
      }
    } else {
      setIsCorrectSubmission(false);
      // playInCorrectSound();
      setModalOpen(true);
      setQuestionScores((prevScores) => [
        ...prevScores,
        {
          questionBankId: currentQuestion.questionBankId,
          questionPaperId: currentQuestion.questionPaperId,
          score: 0,
          layoutNumber: currentQuestion.uiType,
          sectionName: sectionName,
        },
      ]);
    }

    const updatedExamAnswer = {
      examId: 0,
      questionBankId: currentQuestion.questionBankId,
      acutalOptionValue: correctAnswer,
      enteredOptionValue:
        currentQuestion.optionsList?.length > 1
          ? selectedOptions[currentQuestionIndex].join(",")
          : enteredWord,
    };

    // Reset selectedOptions for the next question
    enteredWord = "";

    // Check if there is already an answer for the current question
    const existingAnswerIndex = examAnswers.findIndex(
      (answer) => answer.questionBankId === currentQuestion.questionBankId
    );

    if (existingAnswerIndex !== -1) {
      // If an answer already exists, update the existing one
      setExamAnswers((prevAnswers) =>
        prevAnswers.map((answer, index) =>
          index === existingAnswerIndex ? { ...updatedExamAnswer } : answer
        )
      );
    } else {
      // If there is no existing answer, add the new answer
      setExamAnswers((prevAnswers) => [...prevAnswers, updatedExamAnswer]);
    }

    //console.log("updatedExamAnswer ", updatedExamAnswer);
    //console.log("examAnswers  ", examAnswers);
  };

  const playCorrectSound = () => {
    const correctSound = new Audio(
      require("../assets/correct-choice-43861.mp3")
    );
    correctSound.play();
    handleOpen();
  };

  const playInCorrectSound = () => {
    const incorrectSound = new Audio(
      require("../assets/wronganswer-37702.mp3")
    );
    incorrectSound.play();
    handleOpen();
  };

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);

  const handleContinue = () => {
    setCurrentQuestionIndex((prevIndex) =>
      Math.min(prevIndex + 1, Questions.length - 1)
    );
    setModalOpen(false);
  };

  const handleRetry = () => {
    setInputValues([]);
    setModalOpen(false);
  };

  const handlePaginationChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentQuestionIndex(value - 1);
  };

  useEffect(() => {
    const storageKey = `inputValuesLayout14_${currentQuestion.questionPaperId}`;
    localStorage.setItem(storageKey, JSON.stringify(inputValues));
  }, [inputValues]);

  useEffect(() => {
    if (isViewMode) {
      const correctAnswers = Questions.map((question) => {
        const correctOption = question.optionsList.find(
          (option) => option.correctAnswer === true
        );
        return correctOption ? correctOption.option : "";
      });

      setInputValues(correctAnswers);
      if (currentQuestion.optionsList?.length > 1) {
        const newSelectedOptions = Array(Questions.length).fill(correctAnswers);
        setSelectedOptions(newSelectedOptions);
      }
    }
  }, [isViewMode, Questions]);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "auto",
        // marginTop: isMobile ? "10%" : "3%",
        marginTop:
          isMobile && isViewMode
            ? "20%"
            : isMobile
            ? "10%"
            : isTablet
            ? "15%"
            : isViewMode
            ? "6%"
            : "3%",
      }}
    >
      {/* <CommonModal
        open={modalOpen}
        isCorrectSubmission={isCorrectSubmission}
        handleContinue={handleContinue}
        handleRetry={handleRetry}
        handleClose={handleClose}
      /> */}
      {showAlert && (
        <Alert
          severity="success"
          variant="filled"
          sx={{
            position: "fixed",
            top: "30%",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            padding: "0.5",
          }}
        >
          <AlertTitle sx={{ color: "white" }}>Answer submitted</AlertTitle>
        </Alert>
      )}
      <LayoutWithIcons onNavigate={handleNavigate}>
        {currentQuestion && (
          <Card
            variant="outlined"
            sx={{
              boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
              pointerEvents: isViewMode ? "none" : "auto",
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  paddingBottom: "0px",
                }}
                style={{ userSelect: "none" }}
              >
                <Typography
                  sx={{
                    fontWeight: "700",
                    fontSize: isMobile ? "100%" : "110%",
                  }}
                >
                  {questionText1}
                </Typography>
                {/* <Typography variant="h5">{questionText2}</Typography> */}
                <Fab
                  variant="extended"
                  sx={{
                    fontWeight: isMobile ? 400 : 600,
                    fontSize: isMobile ? "10px" : "auto",
                    cursor: "default",
                    fontFamily: '"Nunito","Segoe UI", sans-serif',
                    backgroundColor: "#E12027 !important",
                    color: "white",
                    p: isMobile && questionText2.length > 65 ? 4 : 2,
                    m: 1,
                    boxShadow: "none",
                  }}
                >
                  {questionText2}
                </Fab>
                {currentQuestion.optionsList?.length > 1 ? (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    {currentQuestion.optionsList.map((option, index) => (
                      <Button
                        key={option.optionId}
                        onClick={() => handleOptionChange(option.option)}
                        variant={
                          selectedOptions[currentQuestionIndex].includes(
                            option.option
                          )
                            ? "contained"
                            : "outlined"
                        }
                        sx={{
                          m: 1,
                          backgroundColor: selectedOptions[
                            currentQuestionIndex
                          ].includes(option.option)
                            ? "#E12027"
                            : undefined,
                          "&:hover": {
                            backgroundColor: selectedOptions[
                              currentQuestionIndex
                            ].includes(option.option)
                              ? "#E12027"
                              : undefined,
                          },
                          ...(isMobile && {
                            padding: "0.1vh 0.5vh",
                            minWidth: "1vh",
                            minHeight: "1vh",
                          }),
                        }}
                      >
                        {option.option}
                      </Button>
                    ))}
                  </Box>
                ) : (
                  <FormControl>
                    <TextField
                      id="filled-basic"
                      label="Type your answer here"
                      sx={{
                        width: "auto",
                      }}
                      value={inputValues[currentQuestionIndex] || ""}
                      onChange={(event) =>
                        handleInputChange(
                          event.target.value,
                          currentQuestionIndex
                        )
                      }
                    />
                  </FormControl>
                )}

                {!isViewMode && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "row",
                      marginTop: "5px",
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      style={{ marginTop: "16px" }}
                      onClick={handleFormSubmit}
                    >
                      Submit
                    </Button>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        )}
        <Grid container lg={12} md={12} sm={12} xs={12} justifyContent="center">
          <Pagination
            count={Questions.length}
            shape="rounded"
            sx={{
              marginBottom: "1vh",
              paddingTop: "4%",
            }}
            page={currentQuestionIndex + 1}
            onChange={handlePaginationChange}
          />
        </Grid>
      </LayoutWithIcons>
    </div>
  );
};

export default Layout14;
